import Add from '@mui/icons-material/Add';
import Close from '@mui/icons-material/Close';
import { Button, IconButton, Input } from 'components/ui';
import {
  DashboardColorSwatch,
  DashboardControlStack,
  DashboardEmptyHint,
  DashboardSortableRowRoot,
} from './dashboardControls.styles';

interface DashboardColorListFieldProps {
  disabled?: boolean;
  onChange: (next: string[]) => void;
  testId?: string;
  value: readonly string[];
}

const DEFAULT_COLOR = '#1f77b4';

const SIX_DIGIT_HEX = /^#[0-9a-f]{6}$/i;
const THREE_DIGIT_HEX = /^#[0-9a-f]{3}$/i;

// <input type="color"> accepts nothing but #rrggbb, so a stored '#abc' is widened to it and anything
// else — a named colour, an rgb() string, whatever an AI wrote — falls back to a neutral swatch. The
// hex field beside it stays editable so such a value can still be read and corrected rather than
// being silently rewritten the moment the row renders.
const toSwatchValue = (raw: string): string => {
  const trimmed = raw.trim();

  if (SIX_DIGIT_HEX.test(trimmed)) {
    return trimmed;
  }
  if (THREE_DIGIT_HEX.test(trimmed)) {
    const [, red, green, blue] = trimmed;

    return `#${red}${red}${green}${green}${blue}${blue}`;
  }

  return DEFAULT_COLOR;
};

// The palette a chart draws from. Entries are positional — series n takes colours[n] — and two
// identical entries are legitimate, which is what rules out the chip list used for tags: it keys and
// deletes by value, so a repeated colour would collide and one delete would remove both.
const DashboardColorListField = ({
  disabled = false,
  onChange,
  testId,
  value,
}: DashboardColorListFieldProps) => {
  const update = (index: number, next: string) =>
    onChange(value.map((entry, entryIndex) => (entryIndex === index ? next : entry)));

  return (
    <DashboardControlStack>
      {value.length === 0 && <DashboardEmptyHint>Using the default palette.</DashboardEmptyHint>}
      {value.map((entry, index) => (
        // Index-keyed on purpose: the value is the editable content of the row, not its identity.
        // eslint-disable-next-line react/no-array-index-key
        <DashboardSortableRowRoot key={`color-${index}`}>
          <DashboardColorSwatch
            aria-label={`Colour ${index + 1}`}
            data-testid={testId ? `${testId}-${index}-picker` : undefined}
            disabled={disabled}
            onChange={event => update(index, event.target.value)}
            type="color"
            value={toSwatchValue(entry)}
          />
          <Input
            disabled={disabled}
            onChange={event => update(index, event.target.value)}
            placeholder={DEFAULT_COLOR}
            size="small"
            value={entry}
            visualVariant="compactOutlined"
            // On the html input rather than the root, so a test can type into it.
            slotProps={{ htmlInput: { 'data-testid': testId ? `${testId}-${index}` : undefined } }}
          />
          <IconButton
            aria-label={`Remove colour ${index + 1}`}
            disabled={disabled}
            onClick={() => onChange(value.filter((_, entryIndex) => entryIndex !== index))}
            size="small">
            <Close fontSize="inherit" />
          </IconButton>
        </DashboardSortableRowRoot>
      ))}
      <div>
        <Button
          data-testid={testId}
          disabled={disabled}
          onClick={() => onChange([...value, DEFAULT_COLOR])}
          sizeVariant="compact"
          startIcon={<Add fontSize="inherit" />}>
          Add colour
        </Button>
      </div>
    </DashboardControlStack>
  );
};

export default DashboardColorListField;
